<template>
  <b-card no-body class="candidate-filter-sidebar" body-class="p-0">
    <h6 class="candidate-filter-sidebar__title px-3 pt-3 mb-0">Filters</h6>
    <category-filter
      label="Trade"
      :categories="tradeOptions"
      :reset="reset"
      searchable
      taggable
      @changed="onTradeChanged"
    />
    <hr class="m-0" />
    <category-filter
      label="Location"
      :categories="locationOptions"
      :reset="reset"
      @changed="onLocationChanged"
    />
    <hr class="m-0" />
    <category-filter
      label="Availability"
      :categories="availabilityOptions"
      :reset="reset"
      @changed="onAvailabilityChanged"
    />
    <hr class="m-0" />
    <category-filter
      label="Employment Type"
      :categories="employmentTypeOptions"
      :reset="reset"
      @changed="onEmploymentTypeChanged"
    />
    <hr class="m-0" />
    <category-filter
      label="Experience"
      :categories="experienceBandOptions"
      :reset="reset"
      @changed="onExperienceBandChanged"
    />
    <hr class="m-0" />
    <category-filter
      label="Ticket"
      :categories="ticketOptions"
      :reset="reset"
      searchable
      multiple
      @changed="onTicketChanged"
    />
    <hr class="m-0" />

    <b-container fluid class="py-3">
      <label class="font-weight-bold d-block mb-2">Sort by</label>
      <b-form-select v-model="sort" :options="sortOptions" />
    </b-container>
    <hr class="m-0" />

    <b-container fluid class="py-3">
      <b-form-checkbox v-model="whiteCard" class="mb-2">
        White Card
      </b-form-checkbox>
      <b-form-checkbox v-model="driverLicence" class="mb-2">
        Driver Licence
      </b-form-checkbox>
      <b-form-checkbox v-model="fifo" class="mb-2"> FIFO Open </b-form-checkbox>
    </b-container>

    <b-container fluid class="pb-3">
      <!-- size="sm" on both: the app's global .btn is 56px tall, with
           .btn-sm specifically overridden down to a compact 32px (see
           src/assets/scss/layouts/_button.scss). Without it here, Apply
           rendered at the full 56px next to Reset's 32px — a jarring size
           mismatch between two buttons doing the same kind of job. -->
      <b-button variant="primary" block size="sm" @click="emitFilters">
        Apply Filters
      </b-button>
      <b-button
        variant="outline-secondary"
        block
        size="sm"
        class="mt-2"
        @click="onReset"
      >
        Reset Filters
      </b-button>
    </b-container>
  </b-card>
</template>

<script>
import CategoryFilter from '@/components/filters/CategoryFilter.vue'

// D9: filter sidebar built from existing filters/* building blocks.
// CategoryFilter (a labelled collapsible select) is reused directly for every
// select-style filter here. LocationFilter and SearchFilter from that folder
// weren't reusable as-is: LocationFilter is wired to Google Places
// (places_id/coordinates), but candidate search's `filter[location]` is a
// plain string against `preferred_locations`; SearchFilter has no matching
// query param in the documented candidate search contract (no free-text
// `search` filter is listed). A plain sort <select> replaces SortByFilter
// too, since that component's sort options are hardcoded to
// price/title and aren't prop-configurable.
export default {
  name: 'CandidateFilterSidebar',

  components: {
    CategoryFilter,
  },

  props: {
    tradeOptions: {
      type: Array,
      default: () => [],
    },
    locationOptions: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      reset: false,
      trade: null,
      location: null,
      availability: null,
      employment_type: null,
      experience_band: null,
      ticket: null,
      whiteCard: false,
      driverLicence: false,
      fifo: false,
      sort: 'availability',

      availabilityOptions: [
        { id: 'immediate', label: 'Available Immediately' },
        { id: 'next_week', label: 'Available Next Week' },
        { id: 'from_date', label: 'Available From Date' },
      ],
      employmentTypeOptions: [
        { id: 'Full Time', label: 'Full Time' },
        { id: 'Casual', label: 'Casual' },
        { id: 'Contract', label: 'Contract' },
        { id: 'FIFO', label: 'FIFO' },
        { id: 'Shutdown', label: 'Shutdown' },
        { id: 'Labour Hire', label: 'Labour Hire' },
        { id: 'Apprenticeship', label: 'Apprenticeship' },
      ],
      experienceBandOptions: [
        { id: '0-2', label: '0–2 Years' },
        { id: '3-5', label: '3–5 Years' },
        { id: '5-10', label: '5–10 Years' },
        { id: '10+', label: '10+ Years' },
      ],
      // Populated from GET templates/getTemplateOptions in created() — see
      // fetchTicketOptions(). Starts empty rather than a hardcoded guess so
      // it can never drift from the real Templates table the way the old
      // 'white-card'/'medical' entries did (one had a typo'd id that never
      // matched anything; the other guessed at a template that didn't exist
      // yet at the time).
      ticketOptions: [],
      // Short labels on purpose — this select sits in the same narrow
      // sidebar column as the other filters, and a native <select> can't
      // wrap its own closed-state text the way the custom ones below can
      // (see CategoryFilter.vue), so a long label just gets clipped mid-word
      // with no ellipsis. Compliance is deliberately not offered here.
      sortOptions: [
        { value: 'availability', text: 'Availability' },
        { value: 'experience', text: 'Experience' },
      ],
    }
  },

  created() {
    this.fetchTicketOptions()
  },

  methods: {
    // Ticket options come from the same Templates table every file-upload
    // template picker in the app already uses (GET templates/getTemplateOptions,
    // {id, label} per template) — not a hardcoded guess. The `ticket` filter
    // itself is matched with LIKE '%value%' against File.name
    // (CandidateSearchService::baseQuery), a free-text field, not template_id
    // — mapping id -> label (not the numeric template id) is what makes the
    // sent value actually match real file names. White Card and Driver
    // Licence are excluded since they already have their own dedicated
    // checkboxes below (and the API only applies one ticket-name filter at a
    // time, so listing them here too could silently override the checkbox).
    // Passport/Birth Certificate/Australian Citizenship/VISA/Proof of Age are
    // excluded as identity documents, not trade tickets.
    async fetchTicketOptions() {
      const EXCLUDED = [
        'White Card',
        'Driver Licence',
        'Passport',
        'Birth Certificate',
        'Australian Citizenship',
        'VISA',
        'Proof of Age',
      ]
      try {
        const { data } = await this.$api.get('templates/getTemplateOptions')
        this.ticketOptions = (data.data || [])
          .filter((t) => !EXCLUDED.includes(t.label))
          .map((t) => ({ id: t.label, label: t.label }))
      } catch (err) {
        // Non-critical filter — leave the list empty rather than surface an
        // error toast for it.
      }
    },

    // These just record the pending value — no emitFilters() here. The
    // search only runs once, when "Apply Filters" is clicked, so picking
    // several filters in a row doesn't fire a search per pick (each
    // CategoryFilter still emits @changed as soon as it collapses, but
    // that's for keeping this component's own state in sync, not for
    // triggering the API call).
    onTradeChanged(payload) {
      this.trade = payload.value
    },
    onLocationChanged(payload) {
      this.location = payload.value
    },
    onAvailabilityChanged(payload) {
      this.availability = payload.value
    },
    onEmploymentTypeChanged(payload) {
      this.employment_type = payload.value
    },
    onExperienceBandChanged(payload) {
      this.experience_band = payload.value
    },
    // Ticket is a multi-select — CategoryFilter emits an array of ids here.
    // The API takes tickets as one comma-separated string (same convention
    // as employment_type), so join it before it lands in currentFilters().
    onTicketChanged(payload) {
      this.ticket = Array.isArray(payload.value)
        ? payload.value.join(',')
        : payload.value
    },
    currentFilters() {
      return {
        trade: this.trade,
        location: this.location,
        availability: this.availability,
        employment_type: this.employment_type,
        experience_band: this.experience_band,
        ticket: this.ticket,
        white_card: this.whiteCard || null,
        driver_licence: this.driverLicence || null,
        fifo: this.fifo || null,
        sort: this.sort,
      }
    },

    // The only two places that actually trigger a search: the "Apply
    // Filters" button (commits whatever's currently picked, however many
    // filters that touches) and onReset below (clearing is unambiguous, so
    // it re-searches immediately rather than waiting for another Apply
    // click).
    emitFilters() {
      this.$emit('changed', this.currentFilters())
    },

    onReset() {
      this.trade = null
      this.location = null
      this.availability = null
      this.employment_type = null
      this.experience_band = null
      this.ticket = null
      this.whiteCard = false
      this.driverLicence = false
      this.fifo = false
      this.sort = 'availability'
      // Pulse `reset` so the CategoryFilter children clear their own
      // internal `selected` state (same contract as tradies/projects
      // filters), then flip it back so the next reset also triggers a watch.
      this.reset = true
      this.$nextTick(() => {
        this.reset = false
      })
      this.emitFilters()
    },
  },
}
</script>

<style lang="scss" scoped>
.candidate-filter-sidebar {
  border-radius: 10px;
}

.candidate-filter-sidebar__title {
  font-weight: 700;
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #6c7581;
}
</style>
